/**
 * AssessmentDelivery.jsx — Phase 2B Offerings Dashboard
 *
 * Admin hub: lists all assessment offerings (DRAFT/PUBLISHED/OPEN/CLOSED/ARCHIVED).
 * Allows creating new offerings from a published blueprint.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, RefreshCw, ChevronRight, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AssessmentDeliveryShell } from "../components/assessment-delivery/AssessmentDeliveryShell";
import { DeliveryStatusBadge } from "../components/assessment-delivery/DeliveryStatusBadge";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
function authH() { return { Authorization: `Bearer ${localStorage.getItem("academiaos_token")}` }; }
async function apiFetch(path, opts = {}) {
  const r = await fetch(`${API}${path}`, { headers: { ...authH(), "Content-Type": "application/json" }, ...opts });
  if (!r.ok) {
    let msg;
    try {
      const body = await r.json();
      msg =
        body?.detail?.message ||
        body?.detail?.conflict ||
        (typeof body?.detail === "string" ? body.detail : null) ||
        body?.message ||
        r.statusText;
    } catch { msg = r.statusText; }
    throw Object.assign(new Error(msg || r.statusText), { status: r.status });
  }
  return r.json();
}

const OFFERING_TYPES = ["REGULAR", "RESIT", "SUPPLEMENTARY", "SPECIAL"];
const TIMEOUT_ACTIONS = ["AUTO_SUBMIT", "ALLOW_OVERTIME", "LOCK_NO_SUBMIT"];

const OFFERING_STATUS_TRANSITIONS = {
  DRAFT:      ["PUBLISHED"],
  PUBLISHED:  ["OPEN", "DRAFT"],
  OPEN:       ["CLOSED", "SUSPENDED"],
  SUSPENDED:  ["OPEN", "CLOSED"],
  CLOSED:     ["ARCHIVED"],
  ARCHIVED:   [],
};

export default function AssessmentDelivery() {
  const [offerings, setOfferings] = useState([]);
  const [blueprints, setBlueprints] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    blueprint_id: "", blueprint_version_no: 1,
    code: "", title: "", description: "",
    offering_type: "REGULAR", max_attempts: 1,
    timeout_action: "AUTO_SUBMIT", duration_override_minutes: "",
  });
  const [saving, setSaving] = useState(false);
  const [statusLoading, setStatusLoading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [offeringList, bpList] = await Promise.all([
        apiFetch("/delivery/offerings"),
        apiFetch("/assessment/blueprints"),
      ]);
      setOfferings(offeringList);
      setBlueprints(bpList.filter(b => b.status === "PUBLISHED"));
    } catch (e) {
      toast.error("Load failed: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.blueprint_id) { toast.error("Select a blueprint"); return; }
    if (!form.code.trim())  { toast.error("Enter an offering code"); return; }
    setSaving(true);
    try {
      const payload = {
        blueprint_id: form.blueprint_id,
        blueprint_version_no: Number(form.blueprint_version_no),
        code: form.code.trim(),
        title: form.title.trim() || form.code.trim(),
        description: form.description.trim() || null,
        offering_type: form.offering_type,
        max_attempts: Number(form.max_attempts) || 1,
        timeout_action: form.timeout_action,
        duration_override_minutes: form.duration_override_minutes
          ? Number(form.duration_override_minutes) : null,
      };
      await apiFetch("/delivery/offerings", { method: "POST", body: JSON.stringify(payload) });
      toast.success("Offering created");
      setShowCreate(false);
      setForm({ blueprint_id: "", blueprint_version_no: 1, code: "", title: "",
                description: "", offering_type: "REGULAR", max_attempts: 1,
                timeout_action: "AUTO_SUBMIT", duration_override_minutes: "" });
      load();
    } catch (e) {
      toast.error("Create failed: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTransition = async (offeringId, newStatus) => {
    setStatusLoading(offeringId);
    try {
      await apiFetch(`/delivery/offerings/${offeringId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      toast.success(`Offering → ${newStatus}`);
      load();
    } catch (e) {
      toast.error("Transition failed: " + e.message);
    } finally {
      setStatusLoading(null);
    }
  };

  const grouped = {
    DRAFT:     offerings.filter(o => o.status === "DRAFT"),
    PUBLISHED: offerings.filter(o => o.status === "PUBLISHED"),
    OPEN:      offerings.filter(o => o.status === "OPEN"),
    CLOSED:    offerings.filter(o => o.status === "CLOSED"),
    ARCHIVED:  offerings.filter(o => o.status === "ARCHIVED"),
  };

  return (
    <AssessmentDeliveryShell title="Offerings Dashboard" breadcrumb="Offerings">
      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-sm text-slate-400">
            {loading ? "Loading…" : `${offerings.length} offering${offerings.length !== 1 ? "s" : ""} across all cohorts`}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="delivery-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400
                       border border-slate-700 bg-slate-900/60 hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(v => !v)}
            data-testid="create-offering-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium
                       text-emerald-300 border border-emerald-700/60 bg-emerald-950/30
                       hover:bg-emerald-900/40 transition-colors"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            New Offering
          </button>
        </div>
      </div>

      {/* ── Create form ──────────────────────────────────────────────────── */}
      {showCreate && (
        <div className="mb-8 rounded-xl border border-emerald-800/40 bg-slate-900/60 p-6"
             data-testid="create-offering-form">
          <h2 className="text-base font-semibold text-slate-100 mb-4">Create Offering</h2>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Blueprint */}
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Published Blueprint *</label>
              <select
                value={form.blueprint_id}
                onChange={e => setForm(f => ({ ...f, blueprint_id: e.target.value }))}
                required
                data-testid="create-offering-blueprint"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600"
              >
                <option value="">— Select a blueprint —</option>
                {blueprints.map(b => (
                  <option key={b.id} value={b.id}>{b.code} — {b.title || b.id}</option>
                ))}
              </select>
            </div>

            {/* Code */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Offering Code *</label>
              <input
                type="text"
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                placeholder="e.g. CS101-2026-SEM1"
                required
                data-testid="create-offering-code"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>

            {/* Title */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. CS101 Final Exam Semester 1"
                data-testid="create-offering-title"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>

            {/* Offering Type */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Type</label>
              <select
                value={form.offering_type}
                onChange={e => setForm(f => ({ ...f, offering_type: e.target.value }))}
                data-testid="create-offering-type"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600"
              >
                {OFFERING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Max Attempts */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Max Attempts</label>
              <input
                type="number"
                min="1" max="10"
                value={form.max_attempts}
                onChange={e => setForm(f => ({ ...f, max_attempts: e.target.value }))}
                data-testid="create-offering-max-attempts"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600"
              />
            </div>

            {/* Timeout action */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">When Time Expires</label>
              <select
                value={form.timeout_action}
                onChange={e => setForm(f => ({ ...f, timeout_action: e.target.value }))}
                data-testid="create-offering-timeout"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600"
              >
                {TIMEOUT_ACTIONS.map(t => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
              </select>
            </div>

            {/* Duration override */}
            <div>
              <label className="block text-xs text-slate-400 mb-1">Duration Override (min)</label>
              <input
                type="number"
                min="1"
                value={form.duration_override_minutes}
                onChange={e => setForm(f => ({ ...f, duration_override_minutes: e.target.value }))}
                placeholder="Leave blank to use session duration"
                data-testid="create-offering-duration"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm
                           text-slate-200 focus:outline-none focus:border-emerald-600 placeholder-slate-600"
              />
            </div>

            {/* Actions */}
            <div className="md:col-span-2 flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                data-testid="create-offering-submit"
                className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-700 hover:bg-emerald-600
                           text-white transition-colors disabled:opacity-50"
              >
                {saving ? "Creating…" : "Create Offering"}
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                data-testid="create-offering-cancel"
                className="px-4 py-2 rounded-lg text-sm text-slate-400 hover:text-slate-200
                           border border-slate-700 hover:border-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Offerings list ───────────────────────────────────────────────── */}
      {loading ? (
        <div className="text-sm text-slate-500 py-12 text-center" data-testid="delivery-loading">
          Loading offerings…
        </div>
      ) : offerings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-700 p-12 text-center"
             data-testid="delivery-empty">
          <p className="text-slate-400 text-sm">No offerings yet.</p>
          <p className="text-slate-600 text-xs mt-1">
            Create a published blueprint first, then add an offering here.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="offerings-list">
          {["OPEN", "PUBLISHED", "DRAFT", "SUSPENDED", "CLOSED", "ARCHIVED"].map(status => {
            const group = offerings.filter(o => o.status === status);
            if (!group.length) return null;
            return (
              <section key={status}>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">
                  {status} ({group.length})
                </h3>
                <div className="space-y-2">
                  {group.map(o => (
                    <OfferingRow
                      key={o.id}
                      offering={o}
                      onTransition={handleTransition}
                      transitionLoading={statusLoading === o.id}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </AssessmentDeliveryShell>
  );
}

function OfferingRow({ offering: o, onTransition, transitionLoading }) {
  const nextStatuses = OFFERING_STATUS_TRANSITIONS[o.status] ?? [];

  return (
    <div className="flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/50
                    px-5 py-4 hover:bg-slate-900 transition-colors group"
         data-testid={`offering-row-${o.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <DeliveryStatusBadge status={o.status} />
          <span className="text-xs text-slate-600 font-mono">{o.code || o.id?.slice(0, 8)}</span>
          <span className="text-xs text-slate-600">{o.offering_type}</span>
        </div>
        <p className="text-sm font-medium text-slate-200 truncate">
          {o.title || o.code || "Untitled Offering"}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          Max {o.max_attempts} attempt{o.max_attempts !== 1 ? "s" : ""}
          {" · "}timeout: {(o.timeout_action || "AUTO_SUBMIT").replace(/_/g, " ")}
        </p>
      </div>

      {/* Transition buttons */}
      {nextStatuses.length > 0 && (
        <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {nextStatuses.map(ns => (
            <button
              key={ns}
              type="button"
              disabled={transitionLoading}
              onClick={() => onTransition(o.id, ns)}
              data-testid={`offering-transition-${o.id}-${ns}`}
              className="px-2.5 py-1 rounded-lg text-xs border transition-colors
                         border-slate-700 text-slate-300 bg-slate-800 hover:bg-slate-700"
            >
              {transitionLoading ? "…" : `→ ${ns}`}
            </button>
          ))}
        </div>
      )}

      {/* Detail link */}
      <Link
        to={`/assessment-delivery/offerings/${o.id}`}
        data-testid={`offering-detail-link-${o.id}`}
        className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300
                   transition-colors shrink-0"
      >
        Manage <ChevronRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}
